import {
  getCusdecMlMessage,
  getCusdecMlRawXml,
  listCusdecMlMessages,
  replayCusdecMlMessage,
  reprocessCusdecMlMessage,
  type CusdecMlListItem,
  type CusdecMlListQuery,
  type CusdecMlRecordDetail,
} from '../api/cusdec-ml';
import { StatusBadge } from '../components/StatusBadge';
import { CorrelationIdField, CustomsMessageConsole } from '../components/CustomsMessageConsole';
import { formatValidationIssue, type ValidationReportSection } from '../lib/customsMessages';

const PAGE_SIZE = 20;

const initialFilters: CusdecMlListQuery = {
  page: 1,
  pageSize: PAGE_SIZE,
  referenceDum: '',
  referenceMessage: '',
  partnerCode: '',
  operation: '',
  processingStatus: '',
};

function validationSections(detail: CusdecMlRecordDetail): ValidationReportSection[] {
  const report = detail.validationReport;
  if (!report) return [];
  return [
    { label: 'Errors', tone: 'error', items: report.errors.map(formatValidationIssue) },
    { label: 'XSD errors', tone: 'error', items: report.xsdErrors.map(formatValidationIssue) },
    { label: 'Warnings', tone: 'warning', items: report.warnings.map(formatValidationIssue) },
  ];
}

export function CusdecMlPage() {
  return (
    <CustomsMessageConsole<CusdecMlListItem, CusdecMlListQuery, CusdecMlRecordDetail>
      eyebrow="Maritime"
      title="CUSDEC ML — DUM Mainlevée"
      purpose="Inspect DUM Mainlevée XML messages received in real time from ADII/PortNet (import and export), their normalized JSON, validation results and processing history."
      status="PRODUCTION_READY"
      next="Use reprocess/replay only after confirming the underlying issue has been resolved."
      listPanelTitle="Mainlevée messages"
      primaryColumnHeader="Reference DUM"
      emptyRowLabel="No CUSDEC ML message matches the current filters."
      detailTitle="Mainlevée detail"
      initialFilters={initialFilters}
      pageSize={PAGE_SIZE}
      listFetch={listCusdecMlMessages}
      detailFetch={getCusdecMlMessage}
      rawXmlFetch={getCusdecMlRawXml}
      getRowId={(item) => item.id}
      getRowLabel={(item) => item.referenceDum ?? item.id}
      filterFields={(filters, patchFilters) => (
        <>
          <label className="filter-field">
            <span>Reference DUM</span>
            <input value={filters.referenceDum ?? ''} onChange={(event) => patchFilters({ referenceDum: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Reference message</span>
            <input value={filters.referenceMessage ?? ''} onChange={(event) => patchFilters({ referenceMessage: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Partner</span>
            <input value={filters.partnerCode ?? ''} onChange={(event) => patchFilters({ partnerCode: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Operation</span>
            <select value={filters.operation ?? ''} onChange={(event) => patchFilters({ operation: event.target.value })}>
              <option value="">All</option>
              <option value="IMPORT">Import</option>
              <option value="EXPORT">Export</option>
              <option value="UNKNOWN">Unknown</option>
            </select>
          </label>
          <label className="filter-field">
            <span>Processing status</span>
            <select value={filters.processingStatus ?? ''} onChange={(event) => patchFilters({ processingStatus: event.target.value })}>
              <option value="">All</option>
              <option value="PROCESSED">Processed</option>
              <option value="REPLAYED">Replayed</option>
              <option value="REJECTED">Rejected</option>
              <option value="QUARANTINED">Quarantined</option>
              <option value="FAILED">Failed</option>
            </select>
          </label>
        </>
      )}
      listColumns={[
        { header: 'Operation', render: (item) => item.operation },
        { header: 'Partner', render: (item) => item.partnerCode ?? 'n/a' },
        { header: 'Date ML', render: (item) => item.dateMlRaw ?? 'n/a' },
        { header: 'Validation', render: (item) => <StatusBadge value={item.validationStatus} /> },
        { header: 'Processing', render: (item) => <StatusBadge value={item.processingStatus} /> },
        { header: 'Received', render: (item) => new Date(item.createdAt).toLocaleString('en-GB') },
      ]}
      detailSummaryFields={(data) => (
        <>
          <div><strong>Operation</strong><span>{data.operation}</span></div>
          <div><strong>Message type</strong><span>{data.messageType}</span></div>
          <div><strong>Root element</strong><span>{data.rootElement}</span></div>
          <div><strong>Filename</strong><span className="mono">{data.filename ?? 'n/a'}</span></div>
          <div><strong>Partner</strong><span>{data.partnerCode ?? 'n/a'}</span></div>
          <div><strong>Reference DUM</strong><span className="mono">{data.referenceDum ?? 'n/a'}</span></div>
          <div><strong>Reference message</strong><span className="mono">{data.referenceMessage ?? 'n/a'}</span></div>
          <div><strong>Version DUM</strong><span>{data.versionDum ?? 'n/a'}</span></div>
          <div><strong>Date ML</strong><span>{data.dateMlRaw ?? 'n/a'}</span></div>
          <div><strong>Message function</strong><span>{data.messageFunctionLabel ?? 'n/a'}</span></div>
          <div><strong>Pesage (weighing)</strong><span>{data.avecPesageMl ?? 'not provided'}</span></div>
          <div><strong>Scanner</strong><span>{data.avecScannerMl ?? 'not provided'}</span></div>
          <div><strong>Declared value</strong><span>{data.valeurDeclare ?? 'n/a'}</span></div>
          <div><strong>Declarant</strong><span className="mono">{data.codeDeclarant ?? 'n/a'}</span></div>
          <div><strong>Soumissionnaire</strong><span className="mono">{data.codeSoumissionnaire ?? 'n/a'}</span></div>
          <div><strong>Validation</strong><span><StatusBadge value={data.validationStatus} /></span></div>
          <div><strong>Processing</strong><span><StatusBadge value={data.processingStatus} /></span></div>
          <CorrelationIdField value={data.correlationId} />
        </>
      )}
      detailChildTables={(data) => (
        <>
          {data.children.articles.length > 0 ? (
            <>
              <h3>Articles</h3>
              <table className="data-table">
                <thead>
                  <tr><th>No.</th><th>Code NGP</th><th>Description</th><th>Origin</th><th>Net weight</th><th>Containers</th></tr>
                </thead>
                <tbody>
                  {data.children.articles.map((article) => (
                    <tr key={article.id}>
                      <td>{article.numeroArticle ?? 'n/a'}</td>
                      <td className="mono">{article.codeNgp ?? 'n/a'}</td>
                      <td>{article.description ?? 'n/a'}</td>
                      <td>{article.paysOrigine ?? article.codePaysOrigine ?? 'n/a'}</td>
                      <td>{article.poidsNet ?? 'n/a'}</td>
                      <td className="mono">{article.containers.map((container) => container.idConteneur).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {data.children.apurementLines.length > 0 ? (
            <>
              <h3>Apurement lines (import)</h3>
              <table className="data-table">
                <thead>
                  <tr><th>Consignataire</th><th>Escale</th><th>DS type/reference</th><th>Lot</th><th>Port</th><th>Status</th><th>Goods lines</th></tr>
                </thead>
                <tbody>
                  {data.children.apurementLines.map((line) => (
                    <tr key={line.id}>
                      <td className="mono">{line.idConsignataire ?? 'n/a'}</td>
                      <td className="mono">{line.numEscale ?? 'n/a'}</td>
                      <td className="mono">{line.typeDsCode ? `${line.typeDsCode} / ${line.referenceDs}` : (line.referenceAndTypeDs ?? 'n/a')}</td>
                      <td>{line.referenceLot ?? 'n/a'}</td>
                      <td>{line.portChargement ?? 'n/a'}</td>
                      <td>{line.statutLabel ?? line.statutCode ?? 'n/a'}</td>
                      <td>{line.goodsLines.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {data.children.seals.length > 0 ? (
            <>
              <h3>Seals (export)</h3>
              <table className="data-table">
                <thead><tr><th>Seal number</th></tr></thead>
                <tbody>
                  {data.children.seals.map((seal) => <tr key={seal.id}><td className="mono">{seal.numScelle}</td></tr>)}
                </tbody>
              </table>
            </>
          ) : null}

          {data.children.transportMeans.length > 0 ? (
            <>
              <h3>Transport means</h3>
              <table className="data-table">
                <thead><tr><th>Type</th><th>Registration</th></tr></thead>
                <tbody>
                  {data.children.transportMeans.map((mean) => (
                    <tr key={mean.id}>
                      <td>{mean.typeMoyenTransport ?? 'n/a'}</td>
                      <td className="mono">{mean.matricule ?? 'n/a'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      )}
      detailValidationReport={validationSections}
      detailActions={(data, helpers) => (
        <>
          <button
            type="button"
            onClick={() => helpers.setConfirm({
              title: 'Reprocess CUSDEC ML message',
              message: 'This re-runs parsing, mapping and validation from the stored raw XML. Raw evidence is never modified.',
              confirmLabel: 'Reprocess',
              risk: 'medium',
              onConfirm: async () => {
                await reprocessCusdecMlMessage(data.id);
                await helpers.refreshDetail();
                helpers.onListChanged();
              },
            })}
          >
            Reprocess
          </button>
          <button
            type="button"
            onClick={() => helpers.setConfirm({
              title: 'Replay CUSDEC ML message',
              message: 'This replays the message through the inbound pipeline using the stored raw XML.',
              confirmLabel: 'Replay',
              risk: 'medium',
              onConfirm: async () => {
                await replayCusdecMlMessage(data.id);
                await helpers.refreshDetail();
                helpers.onListChanged();
              },
            })}
          >
            Replay
          </button>
        </>
      )}
    />
  );
}
